import { ErrorModal, getMomentTz, MesfModal } from "@dexteel/mesf-core";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { Box, Button, Checkbox, Skeleton, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Moment } from "moment";
import { useEffect, useMemo, useState } from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	InfoGrid,
	type InfoGridItem,
	StateChip,
} from "@/pages/sawlines/components/primitives";
import {
	getSawRecentBundles,
	type SawRecentBundle,
} from "@/pages/sawlines/repositories/sawWriteRepository";
import type { WorkOrder } from "@/shared/models/WorkOrder";
import {
	applyWorkOrderTiming,
	getWorkOrders,
	readWorkOrderTimingFromPLC,
} from "@/shared/repositories/WorkOrderRepository";
import { invalidateWorkOrderQueries } from "../hooks/invalidateWorkOrderQueries";
import { WORKORDER_COLOR } from "@/shared/colors";
import { STATUS_COLORS } from "./statusConstants";

const moment = getMomentTz();

type Props = {
	open: boolean;
	workOrderId: number | null;
	assetId: number | null;
	onClose: () => void;
};

const bundleLabel = (b: SawRecentBundle) =>
	`${moment(b.StartTime).format("HH:mm DD/MM")} – ${
		b.EndTime ? moment(b.EndTime).format("HH:mm DD/MM") : "Now"
	} · ${b.BarCount ?? "—"}p`;

const bundleSentence = (b: SawRecentBundle) =>
	`${moment(b.StartTime).format("HH:mm")} – ${
		b.EndTime ? moment(b.EndTime).format("HH:mm") : "now"
	} ${moment(b.StartTime).format("DD/MM")} · ${b.BarCount ?? "—"} pieces`;

const bundleList = (list: SawRecentBundle[]) => {
	const shown = list.slice(0, 8).map(bundleSentence).join("\n");
	return list.length > 8 ? `${shown}\n…and ${list.length - 8} more` : shown;
};

type ConfirmKey = "changes" | "complete" | "reopen" | "plc";
// Confirmations run in this order; each one hands off to the next pending step
const CONFIRM_STEPS: ConfirmKey[] = ["changes", "complete", "reopen"];

// Edit the run window of a Running/Completed order; the window is proposed from the picked bundles
export const WorkOrderTimingModal = ({
	open,
	workOrderId,
	assetId,
	onClose,
}: Props) => {
	const queryClient = useQueryClient();
	// Rows the user clicked: their decision always beats what the window proposes
	const [decided, setDecided] = useState<Map<number, boolean>>(new Map());
	const [lastIndex, setLastIndex] = useState<number | null>(null);
	const [startedAt, setStartedAt] = useState<Moment | null>(null);
	const [completedAt, setCompletedAt] = useState<Moment | null>(null);
	const [confirm, setConfirm] = useState<ConfirmKey | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [initialized, setInitialized] = useState(false);

	const { data: workOrder = null, isInitialLoading: woLoading } = useQuery<
		WorkOrder | null,
		Error
	>({
		queryKey: ["workOrderForTiming", workOrderId],
		queryFn: async () => {
			const rows = await getWorkOrders(
				assetId as number,
				null,
				null,
				null,
				workOrderId,
			);
			return rows[0] ?? null;
		},
		enabled: open && workOrderId != null && assetId != null,
		onError: (e) => setError(e.message),
	});

	// At least the last 5 days of bundles, extended to cover the order's own start
	const daysBack = useMemo(() => {
		if (!workOrder?.StartedAt) return 5;
		return Math.max(5, moment().diff(moment(workOrder.StartedAt), "days") + 2);
	}, [workOrder]);

	const { data: bundles = [], isFetched: bundlesFetched } = useQuery<
		SawRecentBundle[],
		Error
	>({
		queryKey: ["recentBundlesForTiming", assetId, daysBack],
		queryFn: () => getSawRecentBundles(assetId as number, null, 1000, daysBack),
		enabled: open && assetId != null && workOrder != null,
		onError: (e) => setError(e.message),
	});

	const loading = woLoading || (workOrder != null && !bundlesFetched);

	const editable =
		workOrder?.Status === "Running" || workOrder?.Status === "Completed";
	const isRunning = workOrder?.Status === "Running";

	useEffect(() => {
		if (!open) {
			setInitialized(false);
			setConfirm(null);
			setSaving(false);
			setLastIndex(null);
			setDecided(new Map());
		}
	}, [open]);

	// Seed the window once the order lands; the bundle picks derive from it
	useEffect(() => {
		if (!open || initialized || workOrder == null || !bundlesFetched) return;
		setStartedAt(workOrder.StartedAt ? moment(workOrder.StartedAt) : null);
		setCompletedAt(
			workOrder.CompletedAt ? moment(workOrder.CompletedAt) : null,
		);
		setInitialized(true);
	}, [open, initialized, workOrder, bundlesFetched]);

	const origStartMs = workOrder?.StartedAt
		? workOrder.StartedAt.getTime()
		: null;
	const origEndMs = workOrder?.CompletedAt
		? workOrder.CompletedAt.getTime()
		: null;
	const newStartMs = startedAt ? startedAt.valueOf() : null;
	const newEndMs = completedAt ? completedAt.valueOf() : null;
	// The pickers edit down to the minute; ignore sub-second noise so re-deriving
	// the window from a bundle does not count as an edit
	const sameInstant = (a: number | null, b: number | null) =>
		a === b || (a != null && b != null && Math.abs(a - b) < 1000);
	const timesChanged =
		!sameInstant(newStartMs, origStartMs) || !sameInstant(newEndMs, origEndMs);

	const completing = isRunning && newEndMs != null;
	// End cleared on a Completed order = reopen
	const reopening =
		workOrder?.Status === "Completed" && newEndMs == null && origEndMs != null;

	// No end means the order is still running, so the window reaches up to now
	const inNewWindow = (b: SawRecentBundle) =>
		newStartMs != null &&
		b.StartTime.getTime() >= newStartMs &&
		b.StartTime.getTime() < (newEndMs ?? Date.now());

	// Any reclassification (absorbed or not) is pickable only when this order
	// runs the reclassified-to product — mirrors the LinkBundleToWorkOrder guard
	const reclassBlocked = (b: SawRecentBundle) =>
		(b.IsCutback === true || b.ReclassifiedToProductId != null) &&
		!(
			b.IsCutback !== true &&
			b.ReclassifiedToProductId != null &&
			workOrder?.ProductId != null &&
			b.ReclassifiedToProductId === workOrder.ProductId
		);

	// A bundle is free when no order owns it. Reclassified ones are managed from
	// the reclassify screen, so the window never takes them on its own.
	const isFree = (b: SawRecentBundle) =>
		b.WorkOrderId == null && !b.IsReclassified;

	// The ticked rows: the order's own bundles, plus whatever the user picks.
	// Picking moves the window, and the window is what really decides.
	const checked = useMemo(() => {
		const picked = new Set<number>();
		for (const b of bundles) {
			const decision = decided.get(b.BundleId);
			if (decision !== undefined) {
				if (decision) picked.add(b.BundleId);
			} else if (
				b.WorkOrderId === workOrderId &&
				(!editable || inNewWindow(b))
			) {
				picked.add(b.BundleId);
			}
		}
		return picked;
		// biome-ignore lint/correctness/useExhaustiveDependencies: inNewWindow reads the window bounds
	}, [bundles, decided, editable, newStartMs, newEndMs, workOrderId]);

	// Free bundles inside the window that were not ticked: the window takes them
	// anyway, so Save lists them by name and asks first
	const pulledIn = useMemo(
		() =>
			!editable
				? []
				: bundles.filter(
						(b) => !checked.has(b.BundleId) && isFree(b) && inNewWindow(b),
					),
		// biome-ignore lint/correctness/useExhaustiveDependencies: inNewWindow reads the window bounds
		[bundles, checked, editable, newStartMs, newEndMs],
	);

	// Unticked but already the order's and still inside the window: the window
	// keeps them, so the untick alone changes nothing
	const staying = useMemo(
		() =>
			bundles.filter(
				(b) =>
					b.WorkOrderId === workOrderId &&
					!checked.has(b.BundleId) &&
					inNewWindow(b),
			),
		// biome-ignore lint/correctness/useExhaustiveDependencies: inNewWindow reads the window bounds
		[bundles, checked, newStartMs, newEndMs, workOrderId],
	);

	// Save applies exactly this: everything the window and the ticks say belongs
	// here is linked, everything else that belonged is unlinked.
	const toLink = useMemo(
		() =>
			bundles
				.filter((b) => checked.has(b.BundleId) && b.WorkOrderId !== workOrderId)
				.concat(pulledIn),
		[bundles, checked, pulledIn, workOrderId],
	);
	const toUnlink = useMemo(
		() =>
			bundles.filter(
				(b) =>
					b.WorkOrderId === workOrderId &&
					!checked.has(b.BundleId) &&
					!inNewWindow(b),
			),
		// biome-ignore lint/correctness/useExhaustiveDependencies: inNewWindow reads the window bounds
		[bundles, checked, newStartMs, newEndMs, workOrderId],
	);
	// Bundles pulled from another order: that order's window shrinks to what is left
	const stolen = useMemo(
		() => toLink.filter((b) => b.WorkOrderId != null),
		[toLink],
	);

	// Picking bundles re-proposes the window. An order still open stays open: it
	// is closed with Close now or by typing an end, never as a side effect of a click.
	const proposeFrom = (ids: Set<number>) => {
		const picked = bundles.filter((b) => ids.has(b.BundleId));
		if (picked.length === 0) return;
		const first = picked.reduce((a, b) => (a.StartTime <= b.StartTime ? a : b));
		const last = picked.reduce((a, b) => (a.StartTime >= b.StartTime ? a : b));
		setStartedAt(moment(first.StartTime));
		if (completedAt != null)
			setCompletedAt(last.EndTime ? moment(last.EndTime) : null);
	};

	// Shift+click applies the toggled state to the range since the last clicked row
	const handleRowClick = (
		b: SawRecentBundle,
		index: number,
		event: React.MouseEvent,
	) => {
		const value = !checked.has(b.BundleId);
		const ids: number[] = [];
		if (event.shiftKey && lastIndex != null && lastIndex !== index) {
			const [lo, hi] = [Math.min(lastIndex, index), Math.max(lastIndex, index)];
			for (let i = lo; i <= hi; i++) {
				if (!reclassBlocked(bundles[i])) ids.push(bundles[i].BundleId);
			}
		} else {
			ids.push(b.BundleId);
		}
		const next = new Set(checked);
		for (const id of ids) {
			if (value) next.add(id);
			else next.delete(id);
		}
		setDecided((prev) => {
			const map = new Map(prev);
			for (const id of ids) map.set(id, value);
			return map;
		});
		setLastIndex(index);
		proposeFrom(next);
	};

	const windowText = () =>
		`${startedAt?.format("HH:mm DD/MM")} and ${
			completedAt ? completedAt.format("HH:mm DD/MM") : "now"
		}`;

	const changesMessage = () => {
		const parts: string[] = [];
		const picked = toLink.filter((b) => !pulledIn.includes(b));
		if (picked.length > 0)
			parts.push(`Linking to this order:\n${bundleList(picked)}`);
		if (pulledIn.length > 0)
			parts.push(
				`You did not pick these, but they run between ${windowText()} and no order owns them, so they will be linked to this one too:\n${bundleList(pulledIn)}`,
			);
		if (toUnlink.length > 0)
			parts.push(`Removing from this order:\n${bundleList(toUnlink)}`);
		if (staying.length > 0)
			parts.push(
				`These stay in the order because they still run between ${windowText()} — move the start or end to leave them out:\n${bundleList(staying)}`,
			);
		if (stolen.length > 0)
			parts.push(
				`${stolen.length} of them belong to ${[
					...new Set(stolen.map((b) => b.PartNumber ?? "another order")),
				].join(
					", ",
				)}: that order's start/end will be adjusted to its remaining bundles.`,
			);
		return `${parts.join("\n\n")}\n\nContinue?`;
	};

	// Typed values can bypass the picker min/max, so Save re-checks
	const nowMs = Date.now();
	const rangeInvalid =
		(newStartMs != null && newStartMs > nowMs) ||
		(newEndMs != null && newEndMs > nowMs) ||
		(newStartMs != null && newEndMs != null && newStartMs > newEndMs);

	const invalidateAll = () => invalidateWorkOrderQueries(queryClient);

	const save = async () => {
		if (workOrderId == null || workOrder == null) return;
		setSaving(true);
		try {
			const willUpsert = timesChanged || completing || reopening;

			// One SP call runs the unlinks, the links and the timing upsert in a
			// single transaction — a guard rejection leaves nothing half-applied
			if (toUnlink.length > 0 || toLink.length > 0 || willUpsert) {
				const resp = await applyWorkOrderTiming({
					workOrderId,
					startedAt: startedAt ? startedAt.toDate() : null,
					completedAt: completedAt ? completedAt.toDate() : null,
					status: completing ? "Completed" : reopening ? "Running" : null,
					applyTiming: willUpsert,
					unlinkBundleIds: toUnlink.map((b) => b.BundleId),
					linkBundleIds: toLink.map((b) => b.BundleId),
				});
				if (!resp.ok) throw new Error(resp.message);
			}
			await invalidateAll();
			onClose();
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setSaving(false);
		}
	};

	const stepNeeded = (key: ConfirmKey) => {
		if (key === "changes")
			return toLink.length > 0 || toUnlink.length > 0 || staying.length > 0;
		if (key === "complete") return completing;
		if (key === "reopen") return reopening;
		return false;
	};

	// Walk the pending confirmations; once none are left the save runs
	const advanceConfirm = (from: number) => {
		for (let i = from; i < CONFIRM_STEPS.length; i++) {
			if (stepNeeded(CONFIRM_STEPS[i])) {
				setConfirm(CONFIRM_STEPS[i]);
				return;
			}
		}
		setConfirm(null);
		void save();
	};

	const onSaveClick = () => advanceConfirm(0);

	const confirmDone = (key: ConfirmKey) => (value?: string) => {
		setConfirm(null);
		if (value === "OK") advanceConfirm(CONFIRM_STEPS.indexOf(key) + 1);
	};

	const readFromPLC = async () => {
		if (workOrderId == null) return;
		setSaving(true);
		try {
			const result = await readWorkOrderTimingFromPLC(workOrderId, true);
			await invalidateAll();
			if (!result.Applied) {
				setError(result.Message);
			} else {
				// Drop the manual picks too, or they would override the restored links
				setDecided(new Map());
				setLastIndex(null);
				setInitialized(false);
			}
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setSaving(false);
		}
	};

	const statusColors = STATUS_COLORS[workOrder?.Status ?? ""] ?? null;
	const infoItems: InfoGridItem[] = [
		{ label: "Work Order #", value: workOrder?.WorkOrderNumber ?? null },
		{
			label: "Part #",
			value: workOrder?.PartNumber ?? workOrder?.ClipPartNumber ?? null,
		},
		{
			label: "Extended Part #",
			value: workOrder?.ExtendedPartNumber ?? null,
		},
		{
			label: "Pieces",
			value: workOrder?.Quantity != null ? String(workOrder.Quantity) : null,
		},
		{
			label: "Status",
			value: workOrder?.Status ? (
				<Box
					component="span"
					sx={{
						backgroundColor: statusColors?.bg,
						color: statusColors?.color,
						fontWeight: 600,
						fontSize: "0.78rem",
						px: 1,
						py: 0.25,
						borderRadius: 1,
					}}
				>
					{workOrder.Status}
				</Box>
			) : null,
		},
		{ label: "Started By", value: workOrder?.StartedBy ?? null },
		{ label: "Ended By", value: workOrder?.CompletedBy ?? null },
	];

	return (
		<>
			<MesfModal
				id="modal-work-order-timing"
				title="EDIT WORK ORDER TIMES"
				open={open}
				handleClose={onClose}
				maxWidth="md"
			>
				<MesfModal.Content style={{ padding: "15px 24px" }}>
					<Box sx={{ mb: 1.5 }}>
						<InfoGrid
							items={
								loading
									? infoItems.map((it) => ({
											...it,
											value: <Skeleton width={90} />,
										}))
									: infoItems
							}
						/>
					</Box>

					{!editable && workOrder != null && (
						<Typography color="warning.main" sx={{ mb: 1.5 }}>
							Only Running or Completed work orders have a window to edit.
						</Typography>
					)}

					<Box
						sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "center" }}
					>
						{loading ? (
							<>
								<Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
								<Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
							</>
						) : (
							<>
								<DateTimePicker
									label="Start"
									format="MM/DD/YYYY HH:mm"
									ampm={false}
									value={startedAt}
									maxDateTime={completedAt ?? moment()}
									disabled={!editable || saving}
									onChange={(v) => setStartedAt(v)}
									slotProps={{ textField: { size: "small", fullWidth: true } }}
								/>
								<DateTimePicker
									label={isRunning ? "End (completes the order)" : "End"}
									format="MM/DD/YYYY HH:mm"
									ampm={false}
									value={completedAt}
									minDateTime={startedAt ?? undefined}
									maxDateTime={moment()}
									disabled={!editable || saving}
									onChange={(v) => setCompletedAt(v)}
									slotProps={{ textField: { size: "small", fullWidth: true } }}
								/>
							</>
						)}
						{!loading &&
							editable &&
							(completedAt == null ? (
								<Button
									size="small"
									variant="outlined"
									disabled={saving}
									sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
									onClick={() => setCompletedAt(moment())}
								>
									Close now
								</Button>
							) : (
								<Button
									size="small"
									variant="outlined"
									color="warning"
									disabled={saving}
									sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
									// Reopening the end lets the window pick every free bundle since the start
									onClick={() => setCompletedAt(null)}
								>
									Still open
								</Button>
							))}
					</Box>

					<Box
						sx={{
							mt: 0.5,
							height: 400,
							overflow: "auto",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							userSelect: "none",
						}}
					>
						{loading &&
							Array.from({ length: 10 }, (_, i) => (
								<Box
									key={i}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										px: 1,
										py: 0.25,
										borderBottom: "1px solid",
										borderColor: "divider",
									}}
								>
									<Skeleton
										variant="rounded"
										width={18}
										height={18}
										sx={{ m: 0.5, flexShrink: 0 }}
									/>
									<Skeleton width={180} height={24} />
									<Box sx={{ flex: 1 }} />
									<Skeleton width={90} height={24} />
								</Box>
							))}
						{!loading &&
							bundles.map((b, index) => {
								const disabled = reclassBlocked(b) || !editable || saving;
								const own = b.WorkOrderId === workOrderId;
								const highlighted =
									!b.IsReclassified && (own || b.PartNumber != null);
								return (
									<Box
										key={b.BundleId}
										onClick={
											disabled ? undefined : (e) => handleRowClick(b, index, e)
										}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											px: 1,
											py: 0.25,
											borderBottom: "1px solid",
											borderColor: "divider",
											cursor: disabled ? "default" : "pointer",
											opacity: disabled ? 0.5 : 1,
											"&:hover": disabled
												? undefined
												: { backgroundColor: "action.hover" },
										}}
									>
										<Checkbox
											size="small"
											checked={checked.has(b.BundleId)}
											disabled={disabled}
											sx={{ p: 0.5, pointerEvents: "none" }}
										/>
										<Typography
											sx={{
												fontSize: "0.82rem",
												fontWeight: 600,
												flexShrink: 0,
											}}
										>
											{bundleLabel(b)}
										</Typography>
										{b.EndTime == null && (
											<StateChip label="In progress" color="#b58900" />
										)}
										<Typography
											sx={{
												flex: 1,
												minWidth: 0,
												fontSize: "0.78rem",
												fontWeight: highlighted ? 600 : 400,
												color: highlighted ? WORKORDER_COLOR : "text.secondary",
												textAlign: "right",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{b.IsReclassified
												? b.IsCutback
													? "Cutback"
													: `Reclassified to ${b.PartNumber ?? "—"}`
												: own
													? "This order"
													: (b.PartNumber ?? "No work order")}
										</Typography>
									</Box>
								);
							})}
						{!loading && bundles.length === 0 && (
							<Typography
								color="text.secondary"
								sx={{ py: 4, textAlign: "center" }}
							>
								No recent bundles on this line
							</Typography>
						)}
					</Box>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "16px 24px" }}>
					{workOrder?.HasWOTracking && editable && (
						<Button
							variant="outlined"
							startIcon={<SettingsBackupRestoreIcon />}
							disabled={saving}
							onClick={() => setConfirm("plc")}
						>
							Read From PLC
						</Button>
					)}
					<Box sx={{ flexGrow: 1 }} />
					<Button variant="contained" color="inherit" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="success"
						disabled={!editable || saving || startedAt == null || rangeInvalid}
						onClick={onSaveClick}
					>
						{saving ? "Saving…" : "Save"}
					</Button>
				</MesfModal.Actions>
			</MesfModal>

			<ConfirmationDialogRaw
				open={confirm === "changes"}
				title="Apply these bundle changes?"
				message={changesMessage()}
				onClose={confirmDone("changes")}
			/>
			<ConfirmationDialogRaw
				open={confirm === "complete"}
				title="Complete this work order?"
				message="Setting an end on a running order completes it at that time. Continue?"
				onClose={confirmDone("complete")}
			/>
			<ConfirmationDialogRaw
				open={confirm === "reopen"}
				title="Reopen this work order?"
				message="Clearing the end marks the order as still running — its window stays open until it is completed again. Continue?"
				onClose={confirmDone("reopen")}
			/>
			<ConfirmationDialogRaw
				open={confirm === "plc"}
				title="Read times from the PLC?"
				message="This replaces the manual start/end and bundle links with what the historian recorded for this order. Continue?"
				onClose={(value) => {
					setConfirm(null);
					if (value === "OK") void readFromPLC();
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
